import React from 'react';
import {
  Text,
  DetailsList,
  SelectionMode,
  DetailsListLayoutMode,
  IDetailsListProps,
  DetailsRow,
  Stack,
  FontWeights,
  mergeStyleSets,
  IColumn,
} from '@fluentui/react';
import { Image, ImageFit } from '@fluentui/react/lib/Image';

import SystemClient from '../../system/SystemClient';
import MessageCourier from '../../lib/MessageCourier';
import CustomInputIfc from '../../lib/FormValidator/Form/CustomInputIfc';
import PlanillaDigitalForm from './PlanillaDigitalForm';
import { EventoXPlanilla } from '../../system/EventoXPlanilla/EventoXPlanilla';
import DialogTemplate from '../Common/DialogTemplate';

interface IPlanillaDigitalUndo {
  system: SystemClient;
  courier: MessageCourier;
  archivoSyncDescargado: boolean;
  form: PlanillaDigitalForm;
  planillaEventosInput: CustomInputIfc;
  showError: (msg: string) => void;
  changeBlockUI: (state: boolean) => void;
  onBlurCustomInput: (i: CustomInputIfc) => void;
  saveEvents: () => Promise<void>;
}

const PlanillaDigitalUndo = ({
  system,
  courier,
  changeBlockUI,
  form,
  onBlurCustomInput,
  planillaEventosInput,
  saveEvents,
  ...props
}: IPlanillaDigitalUndo) => {
  const [showUndoConfirmation, setShowUndoConfirmation] = React.useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = React.useState<string>('');

  const cancel = () => {
    setShowUndoConfirmation(false);
  };

  const undoEvent = () => {
    if (props.archivoSyncDescargado) {
      return props.showError('No puede deshacer eventos luego de la descarga del archivo de sincronización');
    }
    if (selectedEvent) {
      const _planillaEventos = [...planillaEventosInput.getValue()] as EventoXPlanilla[];
      const idx = _planillaEventos.findIndex((exp) => exp.getIdArtifical() === selectedEvent);
      let eventRemoved;
      if (idx > -1) {
        eventRemoved = _planillaEventos[idx];
      }
      if (eventRemoved && eventRemoved.isSaved()) {
        eventRemoved.undo();
      }
      planillaEventosInput.setValue(_planillaEventos);
      planillaEventosInput.getValue();
      saveEvents();
    }
    setSelectedEvent('');
    setShowUndoConfirmation(false);
  };

  const contentStyles = mergeStyleSets({
    container: {
      display: 'flex',
      flexFlow: 'column nowrap',
      alignItems: 'stretch',
    },
    header: [
      {
        flex: '1 1 auto',
        display: 'flex',
        alignItems: 'center',
        fontWeight: FontWeights.semibold,
      },
    ],
    heading: {
      fontWeight: FontWeights.semibold,
      fontSize: 'inherit',
      margin: '0',
    },
    body: {
      padding: '0 24px 24px 24px',
    },
  });

  const _onRenderEventRow: IDetailsListProps['onRenderRow'] = (props) => {
    if (props) {
      return (
        <div
          onClick={() => {
            setSelectedEvent(props.item.id_artificial_evento);
            setShowUndoConfirmation(true);
          }}
        >
          <DetailsRow
            {...props}
            styles={{
              root: {
                backgroundColor: props.itemIndex % 2 === 0 ? '#f5f5f5' : 'transparent',
                selectors: {
                  ':hover': { backgroundColor: '#d9d8d8' },
                },
              },
            }}
            className="planilla-detail-list"
          />
        </div>
      );
    }
    return null;
  };

  const eventListColumns: IColumn[] = [
    {
      key: 'Club',
      fieldName: 'club',
      name: 'Club',
      minWidth: 50,
      maxWidth: 50,
      onRender: (item: any) => (
        <Image
          src={system.getClubSystem().getUrlForImage(item.club)}
          imageFit={ImageFit.cover}
          maximizeFrame
          styles={{
            root: {
              borderRadius: '50%',
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
              width: '30px',
              height: '30px',
            },
          }}
        />
      ),
    },
    {
      key: 'N°',
      fieldName: 'id',
      name: 'N°',
      minWidth: 50,
      maxWidth: 50,
    },
    {
      key: 'Evento',
      fieldName: 'evento',
      name: 'Evento',
      minWidth: 150,
      onRender: (item: any) => (
        <>
          <Text style={{ display: 'block', fontSize: '13px', fontWeight: 500 }} variant="medium">
            {item.evento}
          </Text>
          <Text style={{ fontSize: '13px', fontWeight: 500 }} variant="xSmall">
            {item.nombre}
          </Text>
        </>
      ),
    },
    {
      key: 'Minuto',
      fieldName: 'minuto',
      name: 'Minuto',
      minWidth: 50,
      maxWidth: 150,
    },
  ];

  const getKey = (item: any, index?: number) => {
    return item.key;
  };

  return (
    <>
      <DetailsList
        items={form.getEventosForList()}
        selectionMode={SelectionMode.none}
        columns={eventListColumns}
        setKey="none"
        layoutMode={DetailsListLayoutMode.justified}
        isHeaderVisible={true}
        getKey={getKey}
        onRenderRow={_onRenderEventRow}
        onShouldVirtualize={() => false}
      />
      <DialogTemplate
        showDialog={showUndoConfirmation}
        toggleShowDialog={() => setShowUndoConfirmation(!showUndoConfirmation)}
        title={'Deshacer evento'}
        onAccept={undoEvent}
        onCancel={cancel}
        acceptText={'Sí'}
        cancelText={'No'}
        minWidth={200}
      >
        <Stack horizontal className={contentStyles.body}>
          <Stack.Item grow>¿Desea deshacer este evento?</Stack.Item>
        </Stack>
      </DialogTemplate>
    </>
  );
};

export default PlanillaDigitalUndo;
